/**
 * Move-out / security-deposit disposition helpers (Cal. Civ. Code § 1950.5) — PURE and shared so
 * the API (finalize, statement PDF) and the admin UI (live totals panel, warnings) compute the SAME
 * numbers from the same inputs. No I/O; every date is a `YYYY-MM-DD` calendar date (the statute
 * counts calendar days, so there is no time-of-day / host-timezone ambiguity to get wrong).
 *
 * Not legal advice — the rules encoded here are the operator's reading of § 1950.5(g); have a
 * California landlord-tenant attorney confirm before relying on them.
 */

/** Deduction categories (mirrors `move_out_deductions.category`). */
export type MoveOutCategory =
  | 'unpaid_rent'
  | 'cleaning'
  | 'repair'
  | 'key_replacement'
  | 'utilities'
  | 'other';

/** Evidence roles (mirrors `move_out_report_documents.role`). */
export type MoveOutDocumentRole =
  | 'move_in_photo'
  | 'move_out_photo'
  | 'post_repair_photo'
  | 'receipt'
  | 'invoice'
  | 'lease'
  | 'other';

/**
 * § 1950.5(g)(2) documentation (copies of bills / invoices / receipts) applies to charges to
 * REPAIR or CLEAN the premises. Key replacement is a repair; `other` is treated as one to stay on
 * the safe side. Unpaid rent and utilities are not repair/cleaning charges, so no receipt is
 * expected for them.
 */
export const MOVE_OUT_DOC_REQUIRED_CATEGORIES: readonly MoveOutCategory[] = [
  'cleaning',
  'repair',
  'key_replacement',
  'other',
];

/**
 * § 1950.5(g)(4)(A)(i): the documentation duty does not apply when repair + cleaning deductions
 * TOGETHER do not exceed $125. Above it, every repair/cleaning charge done by a third party needs
 * its bill/invoice/receipt enclosed.
 */
export const MOVE_OUT_DOC_THRESHOLD_CENTS = 12_500;

/** Human labels for categories (statement PDF + UI). */
export const MOVE_OUT_CATEGORY_LABELS: Record<MoveOutCategory, string> = {
  unpaid_rent: 'Unpaid rent',
  cleaning: 'Cleaning',
  repair: 'Repair',
  key_replacement: 'Key / lock replacement',
  utilities: 'Utilities',
  other: 'Other',
};

/** Human labels for evidence roles. */
export const MOVE_OUT_ROLE_LABELS: Record<MoveOutDocumentRole, string> = {
  move_in_photo: 'Move-in photo',
  move_out_photo: 'Move-out photo',
  post_repair_photo: 'Post-repair photo',
  receipt: 'Receipt',
  invoice: 'Invoice',
  lease: 'Lease',
  other: 'Other',
};

/** Roles that count as a receipt/invoice for the documentation check. */
export const MOVE_OUT_RECEIPT_ROLES: readonly MoveOutDocumentRole[] = ['receipt', 'invoice'];

/** Roles that are photos, and the AB 2801 phase each maps to. */
export const MOVE_OUT_PHOTO_PHASE: Partial<Record<MoveOutDocumentRole, 'move_in' | 'move_out' | 'post_repair'>> = {
  move_in_photo: 'move_in',
  move_out_photo: 'move_out',
  post_repair_photo: 'post_repair',
};

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** True when `v` is a real calendar date in `YYYY-MM-DD` form (rejects 2026-02-30). */
export function isCalendarDate(v: string): boolean {
  const m = DATE_RE.exec(v);
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const dt = new Date(Date.UTC(y, mo - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === mo - 1 && dt.getUTCDate() === d;
}

/** `YYYY-MM-DD` + `days` calendar days → `YYYY-MM-DD` (UTC date math — no DST/host-TZ drift). */
export function addCalendarDays(date: string, days: number): string {
  if (!isCalendarDate(date)) throw new Error(`addCalendarDays: invalid date '${date}'`);
  const [y, mo, d] = date.split('-').map(Number) as [number, number, number];
  const dt = new Date(Date.UTC(y, mo - 1, d + days));
  return dt.toISOString().slice(0, 10);
}

/** Whole calendar days from `from` to `to` (positive when `to` is later). */
export function calendarDaysBetween(from: string, to: string): number {
  const a = Date.parse(`${from}T00:00:00Z`);
  const b = Date.parse(`${to}T00:00:00Z`);
  return Math.round((b - a) / 86_400_000);
}

/**
 * The § 1950.5(g)(1) statement due date: `returnDays` (the `deposit_return_days` rule — 21 in SLO
 * 2026) calendar days after the landlord regained POSSESSION. Not the lease end date.
 */
export function computeStatementDueDate(possessionReturnedDate: string, returnDays: number): string {
  return addCalendarDays(possessionReturnedDate, returnDays);
}

/** Today's calendar date in a given IANA zone (default America/Los_Angeles — the SLO portfolio). */
export function todayInZone(now: Date = new Date(), timeZone = 'America/Los_Angeles'): string {
  // en-CA formats as YYYY-MM-DD.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

export interface MoveOutTotals {
  depositHeldCents: number;
  totalDeductionsCents: number;
  /** The part of the deposit consumed by deductions = min(held, deductions). */
  depositAppliedCents: number;
  /** Returned to the tenant(s) = max(0, held − deductions). */
  refundDueCents: number;
  /** Still owed by the tenant(s) after the whole deposit is applied = max(0, deductions − held). */
  balanceOwedCents: number;
}

/** Integer-cents totals. Exactly one of refund/balance is non-zero (or both zero when equal). */
export function computeMoveOutTotals(
  depositHeldCents: number,
  deductions: readonly { amountCents: number }[],
): MoveOutTotals {
  const held = Math.max(0, Math.trunc(depositHeldCents));
  const total = deductions.reduce((s, d) => s + Math.trunc(d.amountCents), 0);
  return {
    depositHeldCents: held,
    totalDeductionsCents: total,
    depositAppliedCents: Math.min(held, total),
    refundDueCents: Math.max(0, held - total),
    balanceOwedCents: Math.max(0, total - held),
  };
}

/** Owner-labor charge implied by time × rate, rounded to the cent. */
export function ownerLaborCents(minutes: number, rateCents: number): number {
  return Math.round((minutes * rateCents) / 60);
}

export interface MoveOutIssue {
  /** Stable machine code (UI keys + tests). */
  code:
    | 'possession_date_missing'
    | 'recipient_missing'
    | 'recipient_address_missing'
    | 'receipt_missing'
    | 'estimate_vendor_missing'
    | 'owner_labor_amount_mismatch'
    | 'statement_overdue'
    | 'no_deposit_held';
  message: string;
  /** The deduction the issue concerns, when it concerns one. */
  deductionId?: string | null;
}

export interface MoveOutCheckInput {
  possessionReturnedDate: string | null;
  statementDueDate: string | null;
  depositHeldCents: number;
  recipients: readonly { tenantName: string; hasAddress: boolean }[];
  deductions: readonly {
    id: string;
    category: MoveOutCategory;
    description: string;
    amountCents: number;
    isEstimate: boolean;
    vendorInfo: string | null;
    ownerLaborMinutes: number | null;
    ownerLaborRateCents: number | null;
  }[];
  attachments: readonly { role: MoveOutDocumentRole; deductionId: string | null }[];
  /** Today's calendar date (YYYY-MM-DD) for the overdue check. */
  today: string;
}

export interface MoveOutCheckResult {
  /** Finalize is REFUSED (422) while any blocker exists. */
  blockers: MoveOutIssue[];
  /** Surfaced to the owner (and returned by finalize) but never block it. */
  warnings: MoveOutIssue[];
}

const usd = (cents: number) => `$${(cents / 100).toFixed(2)}`;

/**
 * The finalize pre-flight. Blockers: no possession date; no recipient with a mailing address.
 * Warnings (never block): a recipient without an address; repair/cleaning charges above the
 * § 1950.5(g)(4) $125 aggregate with no receipt/invoice linked (third-party, non-estimate items);
 * an estimate with no vendor name/address/phone (§ 1950.5(g)(3)); an owner-labor line whose amount
 * isn't time × rate; the statement being past its 21-day due date; no deposit held.
 */
export function checkMoveOut(input: MoveOutCheckInput): MoveOutCheckResult {
  const blockers: MoveOutIssue[] = [];
  const warnings: MoveOutIssue[] = [];

  if (!input.possessionReturnedDate) {
    blockers.push({
      code: 'possession_date_missing',
      message: 'Enter the date you regained possession — it starts the 21-day statement clock.',
    });
  }

  const withAddress = input.recipients.filter((r) => r.hasAddress);
  if (withAddress.length === 0) {
    blockers.push({
      code: 'recipient_missing',
      message: 'Add at least one tenant recipient with a mailing (forwarding or last known) address.',
    });
  }
  for (const r of input.recipients) {
    if (!r.hasAddress) {
      warnings.push({
        code: 'recipient_address_missing',
        message: `${r.tenantName} has no mailing address — they will not be listed as a recipient.`,
      });
    }
  }

  const docRequiredTotal = input.deductions
    .filter((d) => MOVE_OUT_DOC_REQUIRED_CATEGORIES.includes(d.category))
    .reduce((s, d) => s + d.amountCents, 0);
  const receiptLinked = new Set(
    input.attachments
      .filter((a) => a.deductionId && MOVE_OUT_RECEIPT_ROLES.includes(a.role))
      .map((a) => a.deductionId as string),
  );

  for (const d of input.deductions) {
    const isOwnerLabor = d.ownerLaborMinutes != null && d.ownerLaborRateCents != null;
    if (
      docRequiredTotal > MOVE_OUT_DOC_THRESHOLD_CENTS &&
      MOVE_OUT_DOC_REQUIRED_CATEGORIES.includes(d.category) &&
      !d.isEstimate &&
      !isOwnerLabor &&
      !receiptLinked.has(d.id)
    ) {
      warnings.push({
        code: 'receipt_missing',
        deductionId: d.id,
        message: `“${d.description}” (${usd(d.amountCents)}) has no receipt or invoice attached. Repair and cleaning charges over $125 in total must be supported by copies of the bills (§ 1950.5(g)(2)).`,
      });
    }
    if (d.isEstimate && !isOwnerLabor && !(d.vendorInfo && d.vendorInfo.trim())) {
      warnings.push({
        code: 'estimate_vendor_missing',
        deductionId: d.id,
        message: `“${d.description}” is a good-faith estimate but has no vendor name/address/phone (§ 1950.5(g)(3)).`,
      });
    }
    if (isOwnerLabor) {
      const expected = ownerLaborCents(d.ownerLaborMinutes!, d.ownerLaborRateCents!);
      if (expected !== d.amountCents) {
        warnings.push({
          code: 'owner_labor_amount_mismatch',
          deductionId: d.id,
          message: `“${d.description}” is ${usd(d.amountCents)} but time × rate is ${usd(expected)}.`,
        });
      }
    }
  }

  if (input.statementDueDate && input.today > input.statementDueDate) {
    warnings.push({
      code: 'statement_overdue',
      message: `The statement was due ${input.statementDueDate} (21 days after possession). Retaining any part of the deposit past that date risks a bad-faith claim of up to twice the deposit (§ 1950.5(l)) — get legal advice before sending a late statement.`,
    });
  }

  if (input.depositHeldCents <= 0) {
    warnings.push({
      code: 'no_deposit_held',
      message: 'No deposit is recorded as held — the statement will show only a balance owed.',
    });
  }

  return { blockers, warnings };
}

/* ------------------------------------------------------------------ *
 * 0.27.0 — sign + email the finalized statement.                      *
 * ------------------------------------------------------------------ */

/** Max size of the owner's handwritten signature image (PNG / JPEG). */
export const SIGNATURE_IMAGE_MAX_BYTES = 1_048_576;
/** Max length of the owner's cover note in the delivery email. */
export const MOVE_OUT_COVER_NOTE_MAX = 4000;
/** 0.28.3 — the most blind-copy addresses one statement send may carry (mirrored in `moveOutSendSchema`). */
export const MOVE_OUT_BCC_MAX = 5;
/** 0.28.4 — the most visible-copy (cc) addresses one statement send may carry (mirrored in `moveOutSendSchema`). */
export const MOVE_OUT_CC_MAX = 5;
/** Lifetime of the tokenized packet-download link sent to each recipient. */
export const MOVE_OUT_PACKET_LINK_DAYS = 90;

/**
 * How much of the packet rode along as attachments: `full` = the signed statement + every
 * receipt/invoice copy; `statement_only` = just the signed statement (the copies exceeded the
 * provider's message-size limit — they are behind the download link); `link_only` = nothing
 * attached (even the signed statement alone was too large).
 */
export type MoveOutAttachmentMode = 'full' | 'statement_only' | 'link_only';

/** `jane.doe@example.com` → `ja***@example.com` (display only — never a lookup key). */
export function maskEmail(email: string): string {
  const at = email.lastIndexOf('@');
  if (at <= 0) return '***';
  const local = email.slice(0, at);
  const keep = local.length <= 2 ? 1 : 2;
  return `${local.slice(0, keep)}***${email.slice(at)}`;
}

/**
 * The default delivery-email subject: "<premises short address> — Itemized Statement …".
 * 0.28.1 — `amended` (an earlier revision of this statement was already delivered) prefixes
 * "Amended — "; otherwise the subject is exactly as before.
 */
export function statementEmailSubject(premisesShortAddress: string | null | undefined, amended = false): string {
  const suffix = 'Itemized Statement of Security Deposit (Civ. Code § 1950.5)';
  const addr = (premisesShortAddress ?? '').trim();
  const subject = addr ? `${addr} — ${suffix}` : suffix;
  return amended ? `Amended — ${subject}` : subject;
}

/** The default cover note the UI pre-fills (the owner edits it before sending). */
export function defaultStatementCoverNote(premisesShortAddress: string | null | undefined): string {
  const addr = (premisesShortAddress ?? '').trim();
  return [
    `Enclosed is the signed, itemized statement of your security deposit${addr ? ` for ${addr}` : ''}, provided under California Civil Code § 1950.5. It shows the deposit held, each deduction, and the resulting refund or balance.`,
    'Copies of the supporting receipts and invoices are included with this email or are available at the secure link below.',
    'If you have any questions about the statement, reply to this email.',
  ].join('\n\n');
}

/**
 * Reduce owner-typed text to plain text for an email body: CRLF → LF, tabs → spaces, every other
 * control character dropped, trailing whitespace trimmed per line, at most two blank lines in a row.
 */
export function sanitizePlainText(input: string): string {
  let out = '';
  for (const ch of input.replace(/\r\n?/g, '\n')) {
    const c = ch.codePointAt(0)!;
    if (ch === '\n') out += ch;
    else if (ch === '\t') out += ' ';
    else if (c < 0x20 || (c >= 0x7f && c <= 0x9f) || c === 0x2028 || c === 0x2029) continue;
    else out += ch;
  }
  return out
    .split('\n')
    .map((l) => l.replace(/\s+$/, ''))
    .join('\n')
    .replace(/\n{4,}/g, '\n\n\n')
    .trim();
}
